import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import {
  CollectionFilterRequestBuilder,
  CollectionRequestBuilder,
} from '@web/common';
import {
  PublicBox,
  PublicBoxCategory,
  PublicBoxCharacteristic,
  PublicBoxCollectionFilter,
  PublicBoxCosmetic,
  PublicBoxCosmeticBrand,
  PublicBoxDetails,
  PublicBoxesQuery,
} from '../models';
import { Nillable, valueOrDefault } from '@web/utils';
import { PublicBoxSortType } from '../enums/public-box-sort-type';
import { PublicBoxApiService } from '../api/services/public-box-api.service';
import { PublicBoxAuthService } from './public-box-auth.service';
import { publicSalesFromResponse } from '@web/shared/module/shop';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { PublicBoxComponentGroup } from '../models/public-box-component-group';
import { PublicBoxComponentGroupCosmetic } from '../models/public-box-component-group-cosmetic';

@Injectable({
  providedIn: 'root',
})
export class PublicBoxDataSourceService {
  private readonly publicBoxApi = inject(PublicBoxApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly publicBoxAuth = inject(PublicBoxAuthService);

  public fetchCategories(cacheable = true): Observable<PublicBoxCategory[]> {
    const request = this.collectionRequestBuilder.disablePagination().build();

    return this.publicBoxApi
      .fetchCategoryCollection(request, cacheable)
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new PublicBoxCategory(
                item.id,
                item.slug,
                item.label,
                item.numberOfBoxes,
              ),
          ),
        ),
      );
  }

  public fetchBoxes(
    query?: Nillable<PublicBoxesQuery>,
  ): Observable<PublicBox[]> {
    const request = this.collectionRequestBuilder
      .filters(
        (builder: CollectionFilterRequestBuilder<PublicBoxCollectionFilter>) =>
          builder
            .appendText((builder) =>
              builder.caseIntensiveLikeFor('name').value(query?.phrase).build(),
            )
            .appendText((builder) =>
              builder.equalFor('category').value(query?.categorySlug).build(),
            )
            .build(),
      )
      .sorts((builder) => {
        switch (query?.sortType) {
          case PublicBoxSortType.NameAscending:
            return [builder.ascendingFor('name').build()];

          case PublicBoxSortType.NameDescending:
            return [builder.descendingFor('name').build()];

          case PublicBoxSortType.PublishedAtAscending:
            return [builder.ascendingFor('publishedAt').build()];

          case PublicBoxSortType.PublishedAtDescending:
            return [builder.descendingFor('publishedAt').build()];

          default:
            return [];
        }
      })
      .disablePagination()
      .build();

    return this.publicBoxApi.fetchBoxesCollection({ request }).pipe(
      map((response) =>
        response.items.map(
          (item) =>
            new PublicBox({
              id: item.id,
              slug: item.slug,
              name: item.name,
              publishedAt: item.publishedAt,
              imageAlt: item.logo.alt(),
              imageSrc: item.logo.url,
              rating: item.rating,
              sales: publicSalesFromResponse(item.sales),
              votes: item.numberOfRatings,
            }),
        ),
      ),
      catchError(() => of([])),
    );
  }

  /* prettier-ignore */
  public fetchCarouselBoxes(options?: {
    cacheable?: Nillable<boolean>;
    pageSize?: Nillable<number>;
  }): Observable<PublicBox[]> {
    const today = new Date();
    const yearAgo = new Date(new Date(today).setFullYear(today.getFullYear() - 1));

    const cacheable = valueOrDefault(options?.cacheable, true);
    const request = this.collectionRequestBuilder
      .page(1)
      .filters(
        (builder: CollectionFilterRequestBuilder<PublicBoxCollectionFilter>) =>
          builder
            .appendDate((builder) =>
              builder
                .rangeFor('publishedAt')
                .value({
                  from: yearAgo,
                  to: today,
                })
                .build(),
            )
            .build(),
      )
      .pageSize(valueOrDefault(options?.pageSize, 30))
      .build();

    return this.publicBoxApi.fetchBoxesCollection({ request, cacheable }).pipe(
      map((response) =>
        response.items.map(
          (item) =>
            new PublicBox({
              id: item.id,
              slug: item.slug,
              name: item.name,
              publishedAt: item.publishedAt,
              imageAlt: item.logo.alt(),
              imageSrc: item.logo.url,
              rating: item.rating,
              sales: publicSalesFromResponse(item.sales),
              votes: item.numberOfRatings,
            }),
        ),
      ),
      catchError(() => of([])),
    );
  }

  public fetchCosmeticsBySlug(options: {
    slug: string;
    cacheable?: boolean;
  }): Observable<PublicBoxCosmetic[]> {
    const { cacheable, slug } = options;
    const request = this.collectionRequestBuilder.disablePagination().build();

    return this.publicBoxApi
      .fetchCosmeticCollectionBySlug({
        slug,
        request,
        cacheable: valueOrDefault(cacheable, true),
      })
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new PublicBoxCosmetic({
                id: item.id,
                name: item.name,
                brand: new PublicBoxCosmeticBrand({
                  slug: item.cosmeticBrand.slug,
                  name: item.cosmeticBrand.name,
                  id: item.cosmeticBrand.id,
                }),
                slug: item.slug,
                featuredFile: multimediaFromResponse(item.featuredFile),
              }),
          ),
        ),
        catchError(() => of([])),
      );
  }

  public fetchDetailsBySlug(slug: string): Observable<PublicBoxDetails> {
    return this.publicBoxAuth.fetchAuthorizedDetails(slug).pipe(
      map(
        (response) =>
          new PublicBoxDetails({
            id: response.id,
            slug: response.slug,
            name: response.name,
            price: response.price,
            sales: publicSalesFromResponse(response.sales),
            symbolSrc: response.symbol.url,
            symbolAlt: response.symbol.alt(),
            files: response.galleryFiles.map((file) =>
              multimediaFromResponse(file),
            ),
            illustrationSrc: response.illustration.url,
            illustrationAlt: response.illustration.alt(),
            characteristics: response.characteristics.map(
              (characteristic) =>
                new PublicBoxCharacteristic({
                  content: characteristic.value,
                  label: characteristic.label,
                  id: characteristic.id,
                }),
            ),
            groups: valueOrDefault(response.groups, []).map(
              (group) =>
                new PublicBoxComponentGroup({
                  cosmetics: group.cosmetics.map(
                    (cosmetic) =>
                      new PublicBoxComponentGroupCosmetic({
                        amount: cosmetic.amount,
                        cosmeticBrandName: cosmetic.cosmeticBrand?.name,
                        featureFileAlt: cosmetic.featureFile?.alt(),
                        featureFileUrl: cosmetic.featureFile?.url,
                        id: cosmetic.id,
                        name: cosmetic.name,
                      }),
                  ),
                  description: group.description,
                  id: group.id,
                  isVisibleInQuestionnaire: group.isVisibleInQuestionnaire,
                }),
            ),
            contentPreviewUrl: response.contentPreviewUrl,
          }),
      ),
    );
  }
}
